import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react';
import type { AuthSession } from '../types/auth';
import {
  adminLogin,
  adminLogout,
  adminMe,
  ApiError,
  clearStoredToken,
  getStoredToken,
} from '../lib/api';

interface AuthContextValue {
  session: AuthSession | null;
  user: AuthSession['user'] | null;
  loading: boolean;
  userEmail: string;
  userRole: string;
  signIn: (email: string, password: string) => Promise<void>;
  signOut: () => Promise<void>;
}

const AuthContext = createContext<AuthContextValue | undefined>(undefined);

function sessionFromToken(token: string, user: AuthSession['user']): AuthSession {
  return { access_token: token, user };
}

export function AuthProvider({ children }: { children: ReactNode }) {
  const [session, setSession] = useState<AuthSession | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;

    (async () => {
      const token = getStoredToken();
      if (!token) {
        setLoading(false);
        return;
      }

      try {
        const admin = await adminMe();
        if (!cancelled) {
          setSession(sessionFromToken(token, admin));
        }
      } catch {
        clearStoredToken();
        if (!cancelled) {
          setSession(null);
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    })();

    return () => {
      cancelled = true;
    };
  }, []);

  const user = session?.user ?? null;

  const signIn = useCallback(async (email: string, password: string) => {
    const data = await adminLogin(email, password);
    setSession(sessionFromToken(data.token, data.admin));
  }, []);

  const signOut = useCallback(async () => {
    try {
      await adminLogout();
    } catch (err) {
      if (!(err instanceof ApiError) || err.status !== 401) {
        throw err;
      }
      clearStoredToken();
    }
    setSession(null);
    window.location.href = '/signin';
  }, []);

  const value = useMemo<AuthContextValue>(
    () => ({
      session,
      user,
      loading,
      userEmail: user?.email ?? '',
      userRole: 'admin',
      signIn,
      signOut,
    }),
    [session, user, loading, signIn, signOut],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthContextValue {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}
